import { defineComponent } from "@vue/composition-api";
import { throttle, debounce } from "lodash-es";
import { CreateElement, VNode } from "vue";
import { BlockingStatus } from "@scrile/api-provider/dist/api/BlockingStatusProvider";
import { MessageThread, MessageThreadsSearchResult } from "@scrile/api-provider/dist/api/MessageThreadsProvider";
import { checkAuthorities } from "@scrile/tools/dist/lib/SecurityHelpers";
import useAuthUser from "@/hooks/useAuthUser";
import { useGroups } from "@/hooks/useGroups";
import providers from "@/lib/api";
import emitter from "@/lib/emitter";
import { messageThreadFields } from "@/lib/fieldsHelper";
import { EVENTS } from "@/const";
import MessageThreadsView from "./components/MessageThreadsView.vue";

export default defineComponent({
  name: "PageMessageThreads",
  components: { MessageThreadsView },
  setup() {
    const { authorities: authUserAuthorities, authUserStrict } = useAuthUser();
    const { groups, getData: getGroupsData } = useGroups({ system: true });
    return {
      authUserAuthorities,
      authUserStrict,
      groups,
      getGroupsData,
    };
  },
  data() {
    return {
      scroll: false,
      loading: false,
      filterLoading: false,
      term: "",
      messageThreadsSearchResult: null as MessageThreadsSearchResult | null,
      blockingStatus: [] as BlockingStatus[],
    };
  },
  computed: {
    allowMassMessage(): boolean {
      return this.hasMoreThanOneMembersInGroups;
    },
    messageThreadList(): MessageThread[] {
      return this.messageThreadsSearchResult?.result ?? [];
    },
    authUserId(): string {
      return this.authUserStrict.id;
    },
    hasMoreThanOneMembersInGroups(): boolean {
      return this.groups.some(i => i.memberCount > 1);
    },
  },
  methods: {
    async getGroups() {
      if (checkAuthorities(this.authUserAuthorities, ["updateMyContentPrice"])) this.getGroupsData();
    },
    async getThreads(page = 0, replace = true) {
      this.scroll = false;
      try {
        const response = await providers.MessageThreadsProvider.find(
          {
            data: {
              userId: this.authUserStrict.id,
              sort: "UPDATED_TIME,DESC",
              term: this.term || undefined,
              page,
              size: 20,
            },
          },
          messageThreadFields()
        );
        await this.getBlockingStatusList(response.result);
        this.messageThreadsSearchResult = {
          ...response,
          result:
            replace || !this.messageThreadsSearchResult
              ? response.result
              : this.messageThreadsSearchResult.result.concat(response.result),
        };
      } finally {
        this.scroll = this.messageThreadsSearchResult?.hasNextPage ?? false;
      }
    },
    async getBlockingStatusList(messageThreads: MessageThread[]) {
      if (messageThreads.length === 0 || !messageThreads) {
        return;
      }

      const opponentIds = messageThreads.map(thread =>
        thread.userIds[0] === this.authUserId ? thread.userIds[1] : thread.userIds[0]
      );
      const result = await providers.BlockingStatusProvider.searchByOpponents({ data: { opponentIds } });
      this.blockingStatus = this.blockingStatus.concat(result);
    },
    async getPageData() {
      try {
        this.loading = true;
        await Promise.all([this.getThreads(), this.getGroups()]);
      } finally {
        this.loading = false;
      }
    },
    onMessage() {
      this.getThreads();
    },
    setTerm(term: string) {
      this.term = term;
      this.filterLoading = true;
      this.filterByTerm();
    },
    filterByTerm() {
      this.getThreads().finally(() => (this.filterLoading = false));
    },
    getNextPage(cb: Function) {
      const nextPage = (this.messageThreadsSearchResult?.page ?? 0) + 1;
      this.getThreads(nextPage, false).finally(() => cb());
    },
  },
  render(h: CreateElement): VNode {
    return h(MessageThreadsView, {
      props: {
        loading: this.loading,
        authUserId: this.authUserId,
        allowMassMessage: this.allowMassMessage,
        messageThreadList: this.messageThreadList,
        blockingStatus: this.blockingStatus,
        filterLoading: this.filterLoading,
        term: this.term,
        scroll: this.scroll,
      },
      on: {
        massMessage: () => this.$router.push({ name: "massMessage" }),
        filter: this.setTerm,
        next: this.getNextPage,
      },
    });
  },
  created() {
    this.onMessage = throttle(this.onMessage, 3000, { trailing: false });
    this.filterByTerm = debounce(this.filterByTerm, 500);
    this.getPageData();
    emitter.on<any>(EVENTS.MESSAGES_NEW_MESSAGE, this.onMessage);
    emitter.on<any>(EVENTS.MESSAGES_MARKED_READ, this.onMessage);
  },
  beforeDestroy() {
    emitter.off<any>(EVENTS.MESSAGES_NEW_MESSAGE, this.onMessage);
    emitter.off<any>(EVENTS.MESSAGES_MARKED_READ, this.onMessage);
  },
});
